import React, { useMemo } from "react";
import clsx from "clsx";
import { Row, Col } from "react-bootstrap";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { Field, change } from "redux-form";

import {
	RadioButton,
	DatePickerFieldWrapper,
	DatePickerFromTo,
} from "components";
import { useDatePickerFromTo } from "hooks";
import { staticDropdownValuesService } from "services";
import { float6Normalizer } from "validators";
import { currencySixSigns, onlyPercentToSixFloat } from "validators/validation";

import FeeBlockContext from "../../feeBlockContext";
import TemplateField from "../../templateField";

import styles from "../feesAdditional.module.scss";

const getShowPercentageField = miscFees => {
	return !["0"].includes(miscFees.type);
};

const checkIfValueFilled = value => {
	return !!value && value !== "0.000000";
};

const getMonthFieldDisabled = miscFees => {
	return miscFees.type !== "2";
};

const MiscFeeBlock = ({
	fields,
	index,
	form,
	validators,
	isPaperApplication = false,
}) => {
	const dispatch = useDispatch();

	const { datePickerValuesFrom, datePickerValuesTo } = useDatePickerFromTo();

	const values = useMemo(() => fields.get(index), [fields, index]);
	const isDisabledPercentageField =
		getShowPercentageField(values) ||
		(checkIfValueFilled(values.value) &&
			!checkIfValueFilled(values.percentage));
	const isDisabledCurrencyField =
		checkIfValueFilled(values.percentage) &&
		!checkIfValueFilled(values.value);
	const monthFieldDisabled = getMonthFieldDisabled(values);

	const onTypeChange = e => {
		const { value } = e.target;

		if (value === "2") {
			dispatch(change(form, `${fields.name}[${index}].month`, "Dec"));
		} else {
			dispatch(change(form, `${fields.name}[${index}].month`, ""));
		}

		if (value !== "0") {
			dispatch(change(form, `${fields.name}[${index}].percentage`, ""));
		}
	};

	const onValueChange = fieldName => {
		dispatch(change(form, `${fields.name}[${index}].[${fieldName}]`, ""));
	};

	const { data: feeMonths } = useQuery(
		"getFeeMonths",
		staticDropdownValuesService.getFeeMonths,
	);

	const startDateName = `${fields.name}[${index}].startDate`;
	const endDateName = `${fields.name}[${index}].endDate`;

	return (
		<FeeBlockContext.Provider value={`MiscFee${index + 1}`}>
			<div className={clsx(styles.miscFeeGrid, "mb-4")}>
				<div className={styles.subHeader}>Misc Fee #{index + 1}</div>
				<div>
					<div className="d-flex mb-1" style={{ minHeight: "60px" }}>
						<div className="d-flex flex-column">
							<TemplateField
								name={`${fields.name}[${index}].value`}
								label={<div className="ml-3 pt-1">$</div>}
								wrapperClassName={styles.wrappTemplateField}
								hideRequiredMark
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								placeholder="0.000000"
								placeholderClassName={
									isPaperApplication
										? "placeholder-value"
										: "placeholder-primary"
								}
								disabled={isDisabledCurrencyField}
								onChange={() => onValueChange("percentage")}
							/>
							<span className={styles.warning}>
								Percentage cannot be used with monthly or annual
							</span>
						</div>
						<TemplateField
							name={`${fields.name}[${index}].percentage`}
							label={<div className="ml-5 pt-1">%</div>}
							hideRequiredMark
							wrapperClassName={`${styles.wrappTemplateField} ${styles.wrappPercentageTemplateField} ml-0`}
							normalizeOnBlur={float6Normalizer}
							normalize={onlyPercentToSixFloat}
							placeholder="0.000000"
							placeholderClassName={
								isPaperApplication
									? "placeholder-value"
									: "placeholder-primary"
							}
							disabled={isDisabledPercentageField}
							onChange={() => onValueChange("value")}
							{...validators.percentage}
						/>
					</div>
					<div className={styles.miscFeeRadiosGrid}>
						<Field
							component={RadioButton}
							type="radio"
							name={`${fields.name}[${index}].type`}
							label="Per Item"
							value="0"
							id={`miscTypePerItem${index}`}
							className="pt-2"
							onChange={onTypeChange}
						/>
						<Field
							component={RadioButton}
							type="radio"
							name={`${fields.name}[${index}].type`}
							label="Monthly"
							value="1"
							id={`miscTypeMonthly${index}`}
							className="pt-2"
							onChange={onTypeChange}
						/>
						<Field
							component={RadioButton}
							type="radio"
							name={`${fields.name}[${index}].type`}
							label="Annually"
							value="2"
							id={`miscTypeAnnually${index}`}
							className="pt-2"
							onChange={onTypeChange}
						/>
						<TemplateField
							name={`${fields.name}[${index}].month`}
							label={<p className="">Billed in</p>}
							smSelect={{ span: 10, offset: 2 }}
							disabled={monthFieldDisabled}
							items={feeMonths}
							wrapperClassName={styles.billedInField}
							wrapperDdlClassName={styles.wrapperBilledInFieldDdl}
							type="dropdown"
						/>
					</div>
					<div>
						<div className="mb-3">Dates</div>
						<Row className="mb-3">
							<Col md={6}>
								<Row>
									<Col>
										<Field
											component={DatePickerFieldWrapper}
											datePickerComponent={
												DatePickerFromTo
											}
											name={startDateName}
											id={"startDate" + index}
											placeholderText="Start Date"
											inputFormat="MM/DD/YYYY"
											connectDatePickerFn={
												datePickerValuesFrom
											}
											connectionName="date"
										/>
									</Col>
									<Col>
										<Field
											component={DatePickerFieldWrapper}
											datePickerComponent={
												DatePickerFromTo
											}
											name={endDateName}
											id={"endDate" + index}
											placeholderText="End Date"
											inputFormat="MM/DD/YYYY"
											connectDatePickerFn={
												datePickerValuesTo
											}
											connectionName="date"
										/>
									</Col>
								</Row>
							</Col>
						</Row>
						<TemplateField
							name={`${fields.name}[${index}].description`}
							as="textarea"
							placeholder="Give a brief description"
							rows="3"
						/>
					</div>
				</div>
			</div>
		</FeeBlockContext.Provider>
	);
};

export default MiscFeeBlock;
